import { useEffect, useState, useCallback } from "react";
import { api } from "../../lib/api";
import { useAuth, canEdit } from "../../lib/auth";
import { toast } from "sonner";
import { Plus, Trash2, Edit3, Globe, EyeOff, Pin } from "lucide-react";
import { Modal, TextField, TextArea, SelectField, Toggle } from "./SponsorsPage";

const CATEGORIES = [
  { value: "event_update", label: "Event Update" },
  { value: "press_release", label: "Press Release" },
  { value: "award", label: "Award Announcement" },
  { value: "partnership", label: "Partnership Announcement" },
];

const BLANK = {
  title: "", body: "", category: "event_update",
  image_url: "", link_url: "", published: false, pinned: false,
};

export default function NewsPage() {
  const { user } = useAuth();
  const editable = canEdit(user);
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    const r = await api.get("/admin/news", { withCredentials: true });
    setItems(r.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!editing.title || !editing.body) { toast.error("Title and body required"); return; }
    setSaving(true);
    try {
      const body = { ...editing };
      delete body.id; delete body.created_at; delete body.updated_at;
      if (editing.id) await api.patch(`/admin/news/${editing.id}`, body, { withCredentials: true });
      else await api.post("/admin/news", body, { withCredentials: true });
      toast.success("Saved");
      setEditing(null);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this announcement?")) return;
    await api.delete(`/admin/news/${id}`, { withCredentials: true });
    toast.success("Deleted");
    await load();
  };

  const togglePublish = async (n) => {
    await api.patch(`/admin/news/${n.id}`,
      { ...n, published: !n.published, id: undefined, created_at: undefined, updated_at: undefined },
      { withCredentials: true });
    await load();
  };

  const catLabel = (v) => CATEGORIES.find((c) => c.value === v)?.label || v;

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="text-overline text-aawta-pink mb-2">NEWS & ANNOUNCEMENTS</p>
          <h1 className="font-display text-4xl text-white">News Feed</h1>
          <p className="text-white/50 mt-2 text-sm">Published news appears on the public homepage announcements section.</p>
        </div>
        {editable && (
          <button data-testid="add-news-btn" onClick={() => setEditing({ ...BLANK })} className="btn-pink">
            <Plus size={16} /> NEW ANNOUNCEMENT
          </button>
        )}
      </div>

      <div className="space-y-2">
        {items.length === 0 && (
          <div className="border border-white/10 p-12 text-center text-white/40">No announcements yet.</div>
        )}
        {items.map((n) => (
          <div key={n.id} data-testid={`news-row-${n.id}`} className="border border-white/10 p-5 hover:border-white/20 transition-colors">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3 mb-1.5 text-overline">
                  <span className="text-aawta-pink">{catLabel(n.category)}</span>
                  {n.pinned && <span className="text-yellow-300 flex items-center gap-1"><Pin size={11} /> PINNED</span>}
                </div>
                <h3 className="font-display text-xl text-white">{n.title}</h3>
                <p className="text-sm text-white/60 mt-1 line-clamp-2">{n.body}</p>
              </div>
              <div className="flex items-center gap-3 shrink-0">
                {editable ? (
                  <button onClick={() => togglePublish(n)} className={`inline-flex items-center gap-1.5 text-xs px-2 py-1 border ${n.published ? "border-aawta-pink/50 text-aawta-pink" : "border-white/20 text-white/50"}`}>
                    {n.published ? <Globe size={12} /> : <EyeOff size={12} />}
                    {n.published ? "PUBLISHED" : "DRAFT"}
                  </button>
                ) : <span className="text-xs text-white/50">{n.published ? "Published" : "Draft"}</span>}
                {editable && (
                  <>
                    <button onClick={() => setEditing(n)} className="text-white/60 hover:text-aawta-pink"><Edit3 size={14} /></button>
                    <button onClick={() => remove(n.id)} className="text-white/60 hover:text-red-400"><Trash2 size={14} /></button>
                  </>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <Modal title={editing.id ? "Edit Announcement" : "New Announcement"} onClose={() => setEditing(null)}>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <TextField label="Title *" value={editing.title} onChange={(v) => setEditing({ ...editing, title: v })} testId="news-title" />
            </div>
            <SelectField label="Category" value={editing.category}
              onChange={(v) => setEditing({ ...editing, category: v })}
              options={CATEGORIES.map((c) => c.value)} testId="news-category" />
            <TextField label="Link URL" value={editing.link_url} onChange={(v) => setEditing({ ...editing, link_url: v })} testId="news-link" />
            <div className="md:col-span-2">
              <TextField label="Image URL" value={editing.image_url} onChange={(v) => setEditing({ ...editing, image_url: v })} placeholder="https://…" testId="news-image" />
            </div>
            <div className="md:col-span-2">
              <TextArea label="Body *" value={editing.body} onChange={(v) => setEditing({ ...editing, body: v })} testId="news-body" />
            </div>
            <Toggle label="Published" value={editing.published} onChange={(v) => setEditing({ ...editing, published: v })} testId="news-publish" />
            <Toggle label="Pin to top" value={editing.pinned} onChange={(v) => setEditing({ ...editing, pinned: v })} testId="news-pin" />
          </div>
          <div className="flex justify-end gap-3 mt-6">
            <button onClick={() => setEditing(null)} className="btn-ghost py-2.5">CANCEL</button>
            <button data-testid="save-news-btn" onClick={save} disabled={saving} className="btn-pink disabled:opacity-50">
              {saving ? "SAVING…" : "SAVE"}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}
